import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getPendingTimesheets, getTimesheetHistory, reviewTimesheet, getTimeEntries, getReopenRequests, reviewReopenRequest, deleteTimesheet, reopenTimesheet, getProjects, getMonthInfo } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../lib/errors';
import { CheckSquare, Check, X, Eye, Calendar, History, RefreshCw, AlertCircle, Trash2, Clock } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

export default function ApprovalsPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [timesheets, setTimesheets] = useState([]);
    const [historyTimesheets, setHistoryTimesheets] = useState([]);
    const [reopenRequests, setReopenRequests] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [timesheetToDelete, setTimesheetToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState('pending');
    const [selectedTimesheet, setSelectedTimesheet] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedMonthInfo, setSelectedMonthInfo] = useState(null);
    const [projectsLoaded, setProjectsLoaded] = useState(true);
    const [monthInfoLoaded, setMonthInfoLoaded] = useState(true);
    const [entries, setEntries] = useState([]);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectComment, setRejectComment] = useState('');
    const [processing, setProcessing] = useState(false);
    // Admin-initiated send-back (14.6 / KD-076) — distinct from the reject
    // dialog above: this targets an already-reviewed (approved/rejected)
    // timesheet via the new reopen endpoint, not the pending-review flow.
    const [sendBackDialogOpen, setSendBackDialogOpen] = useState(false);
    const [sendBackComment, setSendBackComment] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [pendingRes, historyRes, reopenRes, projectsRes] = await Promise.all([
                getPendingTimesheets(),
                getTimesheetHistory(),
                getReopenRequests(),
                getProjects({ all: true }).catch(() => null)
            ]);
            setTimesheets(pendingRes.data);
            setHistoryTimesheets(historyRes.data);
            setReopenRequests(reopenRes.data);
            setProjects(projectsRes?.data || []);
            setProjectsLoaded(Boolean(projectsRes?.data));
        } catch (error) {
            toast.error(t('approvals.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const handleReopenAction = async (requestId, action) => {
        setProcessing(true);
        try {
            await reviewReopenRequest(requestId, action);
            toast.success(action === 'approve' ? t('approvals.timesheet_reopened') : t('approvals.request_rejected'));
            fetchData();
        } catch (error) {
            toast.error(t('approvals.failed_load'));
        } finally {
            setProcessing(false);
        }
    };

    const viewDetails = async (timesheet) => {
        setSelectedTimesheet(timesheet);
        try {
            const [res, monthRes] = await Promise.all([
                getTimeEntries({
                    month: timesheet.month,
                    year: timesheet.year,
                    user_id: timesheet.user_id
                }),
                getMonthInfo(timesheet.month, timesheet.year).catch(() => null)
            ]);
            setEntries(res.data);
            setSelectedMonthInfo(monthRes?.data ?? null);
            setMonthInfoLoaded(Boolean(monthRes?.data));
            setDetailsOpen(true);
        } catch (error) {
            toast.error(t('approvals.failed_load'));
        }
    };

    const handleApprove = async (timesheet) => {
        setProcessing(true);
        try {
            await reviewTimesheet({
                timesheet_id: timesheet.id,
                action: 'approve'
            });
            toast.success(t('approvals.timesheet_approved'));
            setDetailsOpen(false);
            fetchData();
        } catch (error) {
            toast.error(t('approvals.failed_approve'));
        } finally {
            setProcessing(false);
        }
    };

    const openRejectDialog = (timesheet) => {
        setSelectedTimesheet(timesheet);
        setRejectComment('');
        setRejectDialogOpen(true);
    };

    const handleReject = async () => {
        if (!rejectComment.trim()) {
            toast.error(t('approvals.provide_reason'));
            return;
        }

        setProcessing(true);
        try {
            await reviewTimesheet({
                timesheet_id: selectedTimesheet.id,
                action: 'reject',
                comment: rejectComment
            });
            toast.success(t('approvals.timesheet_returned'));
            setRejectDialogOpen(false);
            setDetailsOpen(false);
            fetchData();
        } catch (error) {
            toast.error(t('approvals.failed_reject'));
        } finally {
            setProcessing(false);
        }
    };

    const openSendBackDialog = (timesheet) => {
        setSelectedTimesheet(timesheet);
        setSendBackComment('');
        setSendBackDialogOpen(true);
    };

    const handleSendBack = async () => {
        if (!selectedTimesheet || !sendBackComment.trim()) return;

        setProcessing(true);
        try {
            await reopenTimesheet(selectedTimesheet.id, sendBackComment);
            toast.success(t('approvals.send_back_success'));
            setSendBackDialogOpen(false);
            setDetailsOpen(false);
            fetchData();
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('approvals.send_back_failed')));
        } finally {
            setProcessing(false);
        }
    };

    const openDeleteDialog = (timesheet) => {
        setTimesheetToDelete(timesheet);
        setDeleteDialogOpen(true);
    };

    const handleDeleteTimesheet = async () => {
        if (!timesheetToDelete) return;

        setDeleting(true);
        try {
            await deleteTimesheet(timesheetToDelete.id);
            toast.success(t('approvals.timesheet_deleted'));
            setDeleteDialogOpen(false);
            setTimesheetToDelete(null);
            fetchData();
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('approvals.failed_delete')));
        } finally {
            setDeleting(false);
        }
    };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="approvals-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-4xl font-bold tracking-tight">{t('approvals.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('approvals.subtitle')}</p>
            </div>

            {/* Pending Reopen Requests — shown above stats when present (5.91: mirror ExpenseApprovalsPage pattern) */}
            {reopenRequests.length > 0 && (
                <div>
                    <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-4">
                        <RefreshCw className="w-5 h-5" />
                        {t('approvals.reopen_requests_title', { count: reopenRequests.length })}
                    </h2>

                    <div className="space-y-3">
                        {reopenRequests.map((request) => (
                            <Card key={request.id} className="border border-amber-300 bg-amber-50 shadow-none">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium">{request.user_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {monthNames[request.month - 1]} {request.year}
                                            </p>
                                            <p className="text-sm mt-2 italic">
                                                &ldquo;{request.reason}&rdquo;
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {t('approvals.requested', { date: new Date(request.requested_at).toLocaleString() })}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleReopenAction(request.id, 'reject')}
                                                disabled={processing}
                                                className="text-destructive border-destructive"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handleReopenAction(request.id, 'approve')}
                                                disabled={processing}
                                            >
                                                <Check className="w-4 h-4 mr-1" />
                                                {t('approvals.allow_edit')}
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('approvals.stat_pending')}</p>
                                <p className="text-lg sm:text-2xl font-bold font-mono">{timesheets.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('approvals.stat_total_hours')}</p>
                                <p className="text-lg sm:text-2xl font-bold font-mono">{timesheets.reduce((sum, ts) => sum + (ts.total_hours || 0), 0)}h</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('approvals.stat_reviewed')}</p>
                                <p className="text-lg sm:text-2xl font-bold font-mono">{historyTimesheets.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pending / History Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="pending">
                        {t('approvals.tab_pending')} ({timesheets.length})
                    </TabsTrigger>
                    <TabsTrigger value="history">
                        {t('approvals.tab_history')} ({historyTimesheets.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="pending" className="mt-4">
                    {timesheets.length === 0 ? (
                        <Card className="border border-border shadow-none">
                            <CardContent className="p-8 text-center text-muted-foreground">
                                <CheckSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                <p>{t('approvals.no_pending')}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid gap-4">
                            {timesheets.map((timesheet) => (
                                <Card key={timesheet.id} className="border border-border shadow-none">
                                    <CardContent className="p-4">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-foreground flex items-center justify-center flex-shrink-0">
                                                    <Calendar className="w-6 h-6 text-background" strokeWidth={1.5} />
                                                </div>
                                                <div>
                                                    <h3 className="font-heading text-lg font-bold">
                                                        {timesheet.user_name}
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground">
                                                        {monthNames[timesheet.month - 1]} {timesheet.year}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {t('approvals.submitted', { date: new Date(timesheet.submitted_at).toLocaleDateString() })}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center justify-between sm:justify-end gap-3">
                                                <div className="flex items-center gap-4">
                                                    <div className="text-right">
                                                        <p className="font-mono text-xl font-bold">{timesheet.total_hours}h</p>
                                                        <p className="text-xs text-muted-foreground uppercase tracking-widest">
                                                            {t('approvals.total_hours')}
                                                        </p>
                                                    </div>
                                                    {timesheet.total_miles > 0 && (
                                                        <div className="text-right">
                                                            <p className="font-mono text-xl font-bold">{timesheet.total_miles}</p>
                                                            <p className="text-xs text-muted-foreground uppercase tracking-widest">
                                                                {t('approvals.miles')}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => viewDetails(timesheet)}
                                                        data-testid={`view-timesheet-${timesheet.id}`}
                                                    >
                                                        <Eye className="w-4 h-4 mr-2" />
                                                        {t('common.view')}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => openRejectDialog(timesheet)}
                                                        className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                                                        data-testid={`reject-timesheet-${timesheet.id}`}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleApprove(timesheet)}
                                                        disabled={processing}
                                                        data-testid={`approve-timesheet-${timesheet.id}`}
                                                    >
                                                        <Check className="w-4 h-4 mr-2" />
                                                        {t('common.approve')}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="history" className="mt-4">
                    <Card className="border border-border shadow-none">
                        <CardContent className="p-0">
                            {historyTimesheets.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground">
                                    <p>{t('approvals.no_history')}</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>{t('approvals.history_employee')}</th>
                                                <th>{t('approvals.history_period')}</th>
                                                <th>{t('approvals.history_hours')}</th>
                                                <th>{t('approvals.history_miles')}</th>
                                                <th>{t('approvals.history_status')}</th>
                                                <th>{t('approvals.history_reviewed')}</th>
                                                <th>{t('approvals.history_actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyTimesheets.map((ts) => (
                                                <tr key={ts.id} className="opacity-75">
                                                    <td className="font-medium">{ts.user_name}</td>
                                                    <td>{monthNames[ts.month - 1]} {ts.year}</td>
                                                    <td className="font-mono">{ts.total_hours}h</td>
                                                    <td className="font-mono">{ts.total_miles || 0}</td>
                                                    <td>
                                                        <span className={`px-2 py-1 text-xs uppercase tracking-wider ${
                                                            ts.status === 'approved'
                                                                ? 'bg-green-600 text-white'
                                                                : 'bg-destructive text-white'
                                                        }`}>
                                                            {ts.status}
                                                        </span>
                                                    </td>
                                                    <td className="text-sm text-muted-foreground">
                                                        {ts.reviewed_at ? (
                                                            <div>
                                                                <span>{new Date(ts.reviewed_at).toLocaleDateString()}</span>
                                                                {ts.reviewer_name && (
                                                                    <span className="block text-xs">{t('approvals.reviewed_by', { name: ts.reviewer_name })}</span>
                                                                )}
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => viewDetails(ts)}
                                                                title={t('common.view')}
                                                                aria-label={t('common.view')}
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            {/* Only an APPROVED sheet can be sent back: the backend
                                                                accepts submitted/approved only, and a sheet already
                                                                "rejected" is by definition back with the employee —
                                                                offering the action there is a guaranteed 400. */}
                                                            {ts.status === 'approved' && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => openSendBackDialog(ts)}
                                                                    title={t('approvals.send_back_action')}
                                                                    aria-label={t('approvals.send_back_action')}
                                                                    data-testid={`send-back-timesheet-${ts.id}`}
                                                                >
                                                                    <RefreshCw className="w-4 h-4" />
                                                                </Button>
                                                            )}
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => openDeleteDialog(ts)}
                                                                className="text-destructive hover:text-destructive"
                                                                data-testid={`delete-timesheet-${ts.id}`}
                                                                title={t('approvals.delete_timesheet')}
                                                                aria-label={t('approvals.delete_timesheet')}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Details Dialog */}
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="max-w-[96vw] xl:max-w-7xl 2xl:max-w-[1500px] w-full max-h-[90vh] flex flex-col overflow-hidden p-0">
                    <div className="px-6 py-4 border-b border-border flex-shrink-0">
                        <DialogHeader>
                            <DialogTitle className="font-heading text-xl pr-8">
                                {selectedTimesheet && (
                                    <>
                                        {selectedTimesheet.user_name} - {monthNames[selectedTimesheet.month - 1]} {selectedTimesheet.year}
                                    </>
                                )}
                            </DialogTitle>
                        </DialogHeader>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        {/* Summary — three cards optimized for payroll review:
                            Hours (with regular/OT split), Flex impact (net delta
                            with earned/used breakdown — mirrors the timesheet
                            page approval-time logic), and Deviations (total +
                            per-type breakdown like Sjuk/VAB/Semester). Total
                            miles is dropped (mileage is now an expense, not a
                            timesheet stat); entry count is dropped (no admin
                            value). */}
                        {(() => {
                            const totalHours = entries.reduce((s, e) => s + (e.hours || 0), 0);
                            const regularHours = entries.reduce((s, e) => (!e.deviation_type_id && !e.overtime_type ? s + (e.hours || 0) : s), 0);
                            const ot15Hours = entries.reduce((s, e) => (e.overtime_type === 'standard' || e.overtime_type === 'ot1' ? s + (e.hours || 0) : s), 0);
                            const ot2Hours = entries.reduce((s, e) => (e.overtime_type === 'special' || e.overtime_type === 'ot2' || e.overtime_type === 'ot_red' ? s + (e.hours || 0) : s), 0);

                            // Billable project lookup & paid OT set
                            const billableIds = new Set(projects.filter(p => p.billable).map(p => p.id));
                            const OT_PAID = new Set(["ot1", "ot2", "ot_red", "standard", "special"]);

                            // Map expected hours by date from monthInfo
                            const dayBaseByDate = new Map();
                            if (selectedMonthInfo?.days) {
                                for (const d of selectedMonthInfo.days) {
                                    dayBaseByDate.set(d.date, d.expected_hours ?? 8);
                                }
                            }
                            const getDayBase = (dateStr) => {
                                if (dayBaseByDate.has(dateStr)) return dayBaseByDate.get(dateStr);
                                const d = new Date(dateStr + 'T00:00:00Z');
                                const day = d.getUTCDay();
                                return (day === 0 || day === 6) ? 0 : 8;
                            };

                            // Flex impact (mirrors backend approval logic & employee timesheet page)
                            const byDate = new Map();
                            for (const e of entries) {
                                if (!byDate.has(e.date)) byDate.set(e.date, []);
                                byDate.get(e.date).push(e);
                            }
                            let flexEarned = 0;
                            let flexUsed = 0;
                            for (const [dateStr, dayEntries] of byDate.entries()) {
                                let dayWithdraw = 0;
                                let otPaidHours = 0;
                                let flexEligibleHours = 0;
                                let dayTotal = 0;

                                for (const e of dayEntries) {
                                    const hrs = Number(e.hours || 0);
                                    dayTotal += hrs;
                                    const name = (e.deviation_name || '').toLowerCase();
                                    if (e.deviation_type_id && name.startsWith('flex')) {
                                        dayWithdraw += hrs;
                                    } else if (e.project_id && !e.deviation_type_id) {
                                        const isBillable = billableIds.has(e.project_id);
                                        const isOtPaid = isBillable && OT_PAID.has(e.overtime_type);
                                        if (isOtPaid) {
                                            otPaidHours += hrs;
                                        } else {
                                            flexEligibleHours += hrs;
                                        }
                                    }
                                }
                                flexUsed += dayWithdraw;

                                const dayBase = getDayBase(dateStr);
                                const totalForExtra = dayTotal - dayWithdraw;
                                const extra = Math.max(0, totalForExtra - dayBase);
                                if (extra <= 0) continue;

                                const extraAfterOt = Math.max(0, extra - otPaidHours);
                                const dayDeposit = Math.min(extraAfterOt, flexEligibleHours);
                                flexEarned += dayDeposit;
                            }
                            const flexNet = flexEarned - flexUsed;

                            // Deviations grouped by name (excluding flex withdrawals already
                            // counted above — show them under the flex card, not deviations)
                            const deviationByName = new Map();
                            for (const e of entries) {
                                if (!e.deviation_type_id) continue;
                                const name = e.deviation_name || 'Other';
                                if (name.toLowerCase().startsWith('flex')) continue;
                                deviationByName.set(name, (deviationByName.get(name) || 0) + (e.hours || 0));
                            }
                            const deviationsTotal = Array.from(deviationByName.values()).reduce((s, h) => s + h, 0);
                            const fmtH = (h) => Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
                            const fmtSigned = (h) => {
                                if (h === 0) return '0h';
                                const sign = h > 0 ? '+' : '−';
                                const abs = Math.abs(h);
                                return `${sign}${Number.isInteger(abs) ? abs.toFixed(0) : abs.toFixed(1)}h`;
                            };

                            const hoursParts = [];
                            if (regularHours > 0) hoursParts.push(`${t('approvals.regular')} ${fmtH(regularHours)}`);
                            if (ot15Hours > 0) hoursParts.push(`${t('approvals.overtime_15x')} ${fmtH(ot15Hours)}`);
                            if (ot2Hours > 0) hoursParts.push(`${t('approvals.overtime_2x')} ${fmtH(ot2Hours)}`);

                            return (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                                    <div className="p-4 bg-muted">
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{t('approvals.total_hours')}</p>
                                        <p className="font-mono text-2xl font-bold mt-1">{fmtH(totalHours)}</p>
                                        {hoursParts.length > 0 && (
                                            <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
                                                {hoursParts.join(' · ')}
                                            </p>
                                        )}
                                    </div>
                                    <div className="p-4 bg-muted">
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{t('approvals.flex_impact')}</p>
                                        {(!projectsLoaded || !monthInfoLoaded) ? (
                                            <>
                                                <p className="font-mono text-2xl font-bold mt-1 text-muted-foreground">—</p>
                                                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1 leading-tight">
                                                    {t('approvals.flex_data_unavailable', 'Flex data unavailable')}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className={`font-mono text-2xl font-bold mt-1 ${flexNet > 0 ? 'text-green-700 dark:text-green-400' : flexNet < 0 ? 'text-amber-700 dark:text-amber-400' : ''}`}>
                                                    {fmtSigned(flexNet)}
                                                </p>
                                                {(flexEarned > 0 || flexUsed > 0) ? (
                                                    <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
                                                        {t('approvals.flex_breakdown', { earned: fmtH(flexEarned), used: fmtH(flexUsed) })}
                                                    </p>
                                                ) : (
                                                    <p className="text-[11px] text-muted-foreground mt-1 leading-tight">{t('approvals.no_flex_movement')}</p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div className="p-4 bg-muted">
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{t('approvals.deviations_label')}</p>
                                        <p className="font-mono text-2xl font-bold mt-1">{fmtH(deviationsTotal)}</p>
                                        {deviationByName.size > 0 ? (
                                            <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
                                                {Array.from(deviationByName.entries())
                                                    .sort((a, b) => b[1] - a[1])
                                                    .map(([name, hrs]) => `${name} ${fmtH(hrs)}`)
                                                    .join(' · ')}
                                            </p>
                                        ) : (
                                            <p className="text-[11px] text-muted-foreground mt-1 leading-tight">{t('approvals.no_deviations')}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Entries Table — Miles column dropped (mileage is now an
                            expense). Date and project columns get whitespace-nowrap
                            so they don't wrap; notes column is allowed to wrap and
                            takes the slack. */}
                        <div className="overflow-x-auto">
                            <table className="data-table [&_th]:px-3 [&_th]:py-2.5 [&_td]:px-3 [&_td]:py-2 text-xs sm:text-sm">
                                <thead>
                                    <tr>
                                        <th className="whitespace-nowrap">{t('approvals.date')}</th>
                                        <th className="whitespace-nowrap">{t('approvals.project_deviation')}</th>
                                        <th className="whitespace-nowrap">{t('approvals.cost_code')}</th>
                                        <th className="whitespace-nowrap">{t('approvals.activity')}</th>
                                        <th className="whitespace-nowrap">{t('approvals.hours')}</th>
                                        <th className="whitespace-nowrap">{t('approvals.type')}</th>
                                        <th>{t('approvals.notes')}</th>
                                        <th className="text-right whitespace-nowrap">{t('approvals.sum')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from(
                                        entries.reduce((map, entry) => {
                                            if (!map.has(entry.date)) map.set(entry.date, []);
                                            map.get(entry.date).push(entry);
                                            return map;
                                        }, new Map()).entries()
                                    ).sort((a, b) => a[0].localeCompare(b[0])).map(([date, dayEntries]) => (
                                        <React.Fragment key={date}>
                                            {dayEntries.map((entry, idx) => {
                                                const isLast = idx === dayEntries.length - 1;
                                                const daySum = dayEntries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
                                                return (
                                                    <tr key={entry.id} className={isLast ? "border-b-2 border-foreground/50" : "border-b-0"}>
                                                        <td className="font-mono whitespace-nowrap">{entry.date}</td>
                                                        <td className="font-medium whitespace-nowrap">
                                                            {entry.project_name || entry.deviation_name || '-'}
                                                        </td>
                                                        <td className="font-mono whitespace-nowrap text-muted-foreground">
                                                            {entry.project_code || '-'}
                                                        </td>
                                                        <td className="whitespace-nowrap">{entry.activity || '-'}</td>
                                                        <td className="font-mono whitespace-nowrap">{entry.hours}h</td>
                                                        <td className="whitespace-nowrap">
                                                            {entry.deviation_type_id && (
                                                                <span className="time-entry-pill deviation">{t('approvals.deviation')}</span>
                                                            )}
                                                            {(entry.overtime_type === 'standard' || entry.overtime_type === 'ot1') && (
                                                                <span className="time-entry-pill overtime">{t('approvals.overtime_15x')}</span>
                                                            )}
                                                            {(entry.overtime_type === 'special' || entry.overtime_type === 'ot2') && (
                                                                <span className="time-entry-pill overtime">{t('approvals.overtime_2x')}</span>
                                                            )}
                                                            {entry.overtime_type === 'ot_red' && (
                                                                <span className="time-entry-pill ot-red" style={{backgroundColor: 'hsl(0 72% 50%)', color: 'white'}}>{t('timesheet.legend.ot_red')}</span>
                                                            )}
                                                            {entry.overtime_type === 'flex_save' && (
                                                                <span className="time-entry-pill" style={{backgroundColor: '#22c55e', color: 'white'}}>{t('approvals.flex_deposit')}</span>
                                                            )}
                                                            {!entry.deviation_type_id && !entry.overtime_type && (
                                                                <span className="time-entry-pill">{t('approvals.regular')}</span>
                                                            )}
                                                        </td>
                                                        <td className="text-sm text-muted-foreground">
                                                            {entry.notes || '-'}
                                                        </td>
                                                        <td className="text-right font-mono font-medium">
                                                            {isLast ? `${daySum}h` : ''}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t border-border flex-shrink-0">
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                                {t('common.close')}
                            </Button>
                            {/* Only show approve/reject for pending timesheets */}
                            {selectedTimesheet?.status === 'submitted' && (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setDetailsOpen(false);
                                            openRejectDialog(selectedTimesheet);
                                        }}
                                        className="text-destructive border-destructive"
                                    >
                                        <X className="w-4 h-4 mr-2" />
                                        {t('common.reject')}
                                    </Button>
                                    <Button onClick={() => handleApprove(selectedTimesheet)} disabled={processing}>
                                        <Check className="w-4 h-4 mr-2" />
                                        {t('common.approve')}
                                    </Button>
                                </>
                            )}
                            {/* Show status for reviewed timesheets */}
                            {selectedTimesheet?.status === 'approved' && (
                                <div className="px-4 py-2 bg-green-100 text-green-800 text-sm">
                                    <Check className="w-4 h-4 inline mr-1" />
                                    {t('approvals.approved_on', { date: selectedTimesheet.reviewed_at ? new Date(selectedTimesheet.reviewed_at).toLocaleDateString() : '' })}
                                </div>
                            )}
                            {selectedTimesheet?.status === 'rejected' && (
                                <div className="px-4 py-2 bg-red-100 text-destructive text-sm">
                                    <X className="w-4 h-4 inline mr-1" />
                                    {t('approvals.rejected_on', { date: selectedTimesheet.reviewed_at ? new Date(selectedTimesheet.reviewed_at).toLocaleDateString() : '' })}
                                </div>
                            )}
                            {/* Already-reviewed timesheets get a send-back action instead
                                of approve/reject (14.6 / KD-076) */}
                            {selectedTimesheet && selectedTimesheet.status === 'approved' && (
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setDetailsOpen(false);
                                        openSendBackDialog(selectedTimesheet);
                                    }}
                                    data-testid="send-back-timesheet-details-btn"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    {t('approvals.send_back_action')}
                                </Button>
                            )}
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('approvals.return_for_corrections')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('approvals.return_for_corrections_hint')}
                        </p>
                        <Textarea
                            value={rejectComment}
                            onChange={(e) => setRejectComment(e.target.value)}
                            placeholder={t('approvals.rejection_placeholder')}
                            rows={4}
                            data-testid="reject-comment-input"
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleReject}
                            disabled={processing}
                            data-testid="confirm-reject-btn"
                        >
                            {processing ? t('common.processing') : t('approvals.confirm_return')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Send Back Dialog — admin-initiated correction request on an
                already-reviewed timesheet (14.6 / KD-076) */}
            <Dialog open={sendBackDialogOpen} onOpenChange={setSendBackDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('approvals.send_back_title')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('approvals.send_back_hint')}
                        </p>
                        <Textarea
                            value={sendBackComment}
                            onChange={(e) => setSendBackComment(e.target.value)}
                            placeholder={t('approvals.send_back_placeholder')}
                            rows={4}
                            data-testid="send-back-comment-input"
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSendBackDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleSendBack}
                            disabled={processing || !sendBackComment.trim()}
                            data-testid="confirm-send-back-btn"
                        >
                            {processing ? t('common.processing') : t('approvals.confirm_send_back')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2 text-destructive">
                            <AlertCircle className="w-5 h-5" />
                            {t('approvals.delete_timesheet')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('approvals.delete_confirm')}
                        </p>

                        {timesheetToDelete && (
                            <div className="p-4 bg-muted rounded-lg space-y-2">
                                <p><strong>{t('approvals.employee')}</strong> {timesheetToDelete.user_name}</p>
                                <p><strong>{t('approvals.period')}</strong> {monthNames[timesheetToDelete.month - 1]} {timesheetToDelete.year}</p>
                                <p><strong>{t('approvals.hours_label')}</strong> {timesheetToDelete.total_hours}h</p>
                                <p><strong>{t('approvals.status_label')}</strong> {timesheetToDelete.status}</p>
                            </div>
                        )}

                        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
                            <strong>{t('approvals.warning')}</strong> {t('approvals.delete_warning')}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteTimesheet}
                            disabled={deleting}
                            data-testid="confirm-delete-timesheet-btn"
                        >
                            {deleting ? t('common.deleting') : t('approvals.delete_timesheet')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
